import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Building2, Users, Upload, Save, ArrowLeft, Plus, Trash2, 
  Edit2, Key, Mail, UserCheck, UserX, Eye, EyeOff,
  CreditCard, ExternalLink, AlertTriangle, Calendar, Sparkles,
  MessageSquare, Bug, Lightbulb, Send, MapPin, Phone, Crown, Server
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanySettings() {
  const { user, token, companyContext } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [familyAutoEnable, setFamilyAutoEnable] = useState(true);
  const [familyFeatureEnabled, setFamilyFeatureEnabled] = useState(false);
  const [savingFamilyDefault, setSavingFamilyDefault] = useState(false);
  
  // Company form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    vat_number: '',
    bramm_number: '',
    namm_number: '',
    email_footer: ''
  });
  
  // Branding form
  const [brandingData, setBrandingData] = useState({
    tagline: '',
    footer_text: '',
    bank_name: '',
    bank_account: '',
    sort_code: ''
  });
  
  // User dialogs
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  
  // Feedback/Support dialog
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feature'); // feature, bug, other
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff'
  });
  
  const [newPassword, setNewPassword] = useState('');
  
  // Branch management state
  const [branches, setBranches] = useState([]);
  const [branchLimits, setBranchLimits] = useState(null);
  const [showBranchDialog, setShowBranchDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [branchForm, setBranchForm] = useState({
    name: '',
    short_code: '',
    address: '',
    phone: '',
    email: ''
  });
  
  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: ''
  });
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [emailSettingsConfigured, setEmailSettingsConfigured] = useState(false);
  
  // Industry-specific email settings (for companies handling both)
  const [emailSettingsMemorials, setEmailSettingsMemorials] = useState({
    smtp_host: '', smtp_port: 465, smtp_user: '', smtp_password: '', from_email: '', from_name: ''
  });
  const [emailSettingsWorktops, setEmailSettingsWorktops] = useState({
    smtp_host: '', smtp_port: 465, smtp_user: '', smtp_password: '', from_email: '', from_name: ''
  });
  const [emailSettingsMemorialsConfigured, setEmailSettingsMemorialsConfigured] = useState(false);
  const [emailSettingsWorktopsConfigured, setEmailSettingsWorktopsConfigured] = useState(false);
  const [savingMemorialsEmail, setSavingMemorialsEmail] = useState(false);
  const [savingWorktopsEmail, setSavingWorktopsEmail] = useState(false);
  const [testingMemorialsEmail, setTestingMemorialsEmail] = useState(false);
  const [testingWorktopsEmail, setTestingWorktopsEmail] = useState(false);
  const [showMemorialsPassword, setShowMemorialsPassword] = useState(false);
  const [showWorktopsPassword, setShowWorktopsPassword] = useState(false);
  
  // Super admins can view other companies via URL param
  const urlCompanyId = searchParams.get('company');
  const companyId = urlCompanyId || companyContext?.company_id || user?.company_id;
  const isViewingOtherCompany = urlCompanyId && urlCompanyId !== (companyContext?.company_id || user?.company_id);
  
  // Subscription helpers
  const getTrialDaysRemaining = () => {
    if (!company?.subscription?.trial_ends_at) return 0;
    const trialEnd = new Date(company.subscription.trial_ends_at);
    const now = new Date();
    const days = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };
  
  const handleOpenBillingPortal = async () => {
    try {
      setOpeningPortal(true);
      const response = await axios.post(`${BACKEND_URL}/api/subscriptions/portal`, {
        company_id: companyId,
        return_url: window.location.href
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe billing portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to open billing portal');
      toast.error(message);
      setOpeningPortal(false);
    }
  };
  
  useEffect(() => {
    if (!user || !['super_admin', 'company_admin', 'admin', 'demo'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, companyId]);
  
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/family-page/company-defaults`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setFamilyAutoEnable(res.data.auto_enable !== false);
      setFamilyFeatureEnabled(res.data.feature_enabled === true);
    }).catch(() => {});
  }, [token]);
  
  const handleToggleFamilyFeature = async (value) => {
    setFamilyFeatureEnabled(value);
    try {
      setSavingFamilyDefault(true);
      await axios.patch(`${BACKEND_URL}/api/family-page/company-defaults`, { feature_enabled: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(value ? 'Family Pages turned on' : 'Family Pages turned off');
    } catch (error) {
      setFamilyFeatureEnabled(!value);
      toast.error('Failed to save setting');
    } finally {
      setSavingFamilyDefault(false);
    }
  };
  
  const handleToggleFamilyDefault = async (value) => {
    setFamilyAutoEnable(value);
    try {
      setSavingFamilyDefault(true);
      await axios.patch(`${BACKEND_URL}/api/family-page/company-defaults`, { auto_enable: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(value ? 'Family pages will be created automatically' : 'Automatic family pages turned off');
    } catch (error) {
      setFamilyAutoEnable(!value);
      toast.error('Failed to save setting');
    } finally {
      setSavingFamilyDefault(false);
    }
  };
  
  const fetchData = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const [companyRes, usersRes, brandingRes, branchesRes, branchLimitsRes, emailSettingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies/${companyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/companies/${companyId}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/companies/${companyId}/branding`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/branches`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/branches/limits`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/companies/${companyId}/email-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} }))
      ]);
      
      setCompany(companyRes.data);
      setUsers(usersRes.data);
      setBranches(branchesRes.data?.branches || branchesRes.data || []);
      setBranchLimits(branchLimitsRes.data);
      
      setFormData({
        name: companyRes.data.name || '',
        email: companyRes.data.email || '',
        phone: companyRes.data.phone || '',
        address: companyRes.data.address || '',
        website: companyRes.data.website || '',
        vat_number: companyRes.data.vat_number || '',
        bramm_number: companyRes.data.bramm_number || '',
        namm_number: companyRes.data.namm_number || '',
        email_footer: companyRes.data.branding?.email_footer || ''
      });
      
      setBrandingData({
        tagline: brandingRes.data?.tagline || 'Handcrafted Memorials',
        footer_text: brandingRes.data?.footer_text || 'If you have any questions, please don\'t hesitate to contact us.',
        bank_name: brandingRes.data?.bank_name || companyRes.data.name || '',
        bank_account: brandingRes.data?.bank_account || '',
        sort_code: brandingRes.data?.sort_code || ''
      });
      
      // Set email settings
      if (emailSettingsRes.data) {
        setEmailSettings({
          smtp_host: emailSettingsRes.data.smtp_host || '',
          smtp_port: emailSettingsRes.data.smtp_port || 465,
          smtp_user: emailSettingsRes.data.smtp_user || '',
          smtp_password: '', // Never returned from server for security
          from_email: emailSettingsRes.data.from_email || '',
          from_name: emailSettingsRes.data.from_name || ''
        });
        setEmailSettingsConfigured(emailSettingsRes.data.is_configured || false);
      }
      
      // Fetch industry-specific email settings for companies handling both
      if (companyRes.data.industry_type === 'both') {
        try {
          const [memorialsRes, worktopsRes] = await Promise.all([
            axios.get(`${BACKEND_URL}/api/companies/${companyId}/email-settings/memorials`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: {} })),
            axios.get(`${BACKEND_URL}/api/companies/${companyId}/email-settings/worktops`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => ({ data: {} }))
          ]);
          
          if (memorialsRes.data) {
            setEmailSettingsMemorials({
              smtp_host: memorialsRes.data.smtp_host || '',
              smtp_port: memorialsRes.data.smtp_port || 465,
              smtp_user: memorialsRes.data.smtp_user || '',
              smtp_password: '',
              from_email: memorialsRes.data.from_email || '',
              from_name: memorialsRes.data.from_name || ''
            });
            setEmailSettingsMemorialsConfigured(memorialsRes.data.is_configured || false);
          }
          
          if (worktopsRes.data) {
            setEmailSettingsWorktops({
              smtp_host: worktopsRes.data.smtp_host || '',
              smtp_port: worktopsRes.data.smtp_port || 465,
              smtp_user: worktopsRes.data.smtp_user || '',
              smtp_password: '',
              from_email: worktopsRes.data.from_email || '',
              from_name: worktopsRes.data.from_name || ''
            });
            setEmailSettingsWorktopsConfigured(worktopsRes.data.is_configured || false);
          }
        } catch (err) {
          console.log('Industry-specific email settings not available');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveCompany = async () => {
    try {
      setSaving(true);
      await axios.put(`${BACKEND_URL}/api/companies/${companyId}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        address: formData.address || null,
        website: formData.website || null,
        vat_number: formData.vat_number || null,
        bramm_number: formData.bramm_number || null,
        namm_number: formData.namm_number || null,
        branding: {
          email_footer: formData.email_footer || null
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company settings saved');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save'); toast.error(msg);
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveBranding = async () => {
    try {
      setSavingBranding(true);
      await axios.put(`${BACKEND_URL}/api/companies/${companyId}/branding`, brandingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branding settings saved');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save branding'); toast.error(msg);
    } finally {
      setSavingBranding(false);
    }
  };

  // Email settings functions
  const handleSaveEmailSettings = async () => {
    try {
      setSavingEmailSettings(true);
      
      // Get the actual password value from DOM in case of browser autofill
      const passwordInput = document.querySelector('[data-testid="smtp-password-input"]');
      const actualPassword = passwordInput?.value || emailSettings.smtp_password || '';
      
      // Only include password if user entered a new one
      const dataToSave = {
        smtp_host: emailSettings.smtp_host || null,
        smtp_port: emailSettings.smtp_port || 465,
        smtp_user: emailSettings.smtp_user || null,
        from_email: emailSettings.from_email || null,
        from_name: emailSettings.from_name || null
      };
      
      // Only send password if it was changed (not empty)
      if (actualPassword) {
        dataToSave.smtp_password = actualPassword;
        console.log('Saving password (length:', actualPassword.length, ')');
      } else {
        console.log('No password to save - field is empty');
      }
      
      console.log('Sending email settings:', Object.keys(dataToSave));
      console.log('Password included:', !!dataToSave.smtp_password);
      
      const response = await axios.put(`${BACKEND_URL}/api/companies/${companyId}/email-settings`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Save response:', response.data);
      
      if (response.data.password_saved) {
        toast.success('Email settings saved (password updated!)');
      } else if (actualPassword) {
        // User tried to save password but it wasn't saved
        toast.warning('Settings saved but password may not have been updated - check logs');
      } else {
        toast.success('Email settings saved');
      }
      // Clear the password field after saving
      setEmailSettings(prev => ({ ...prev, smtp_password: '' }));
      if (passwordInput) passwordInput.value = ''; // Also clear DOM
      fetchData(); // Refresh to get updated is_configured status
    } catch (error) {
      console.error('Save error:', error.response?.data || error);
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to save email settings'); toast.error(msg);
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      const res = await axios.post(`${BACKEND_URL}/api/companies/${companyId}/email-settings/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Test email sent!');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send test email'); toast.error(msg);
    } finally {
      setTestingEmail(false);
    }
  };

  // Industry-specific email settings handlers
  const handleSaveIndustryEmailSettings = async (industryType) => {
    const settings = industryType === 'memorials' ? emailSettingsMemorials : emailSettingsWorktops;
    const setSaving = industryType === 'memorials' ? setSavingMemorialsEmail : setSavingWorktopsEmail;
    const setSettings = industryType === 'memorials' ? setEmailSettingsMemorials : setEmailSettingsWorktops;
    const testId = industryType === 'memorials' ? 'memorials-smtp-password' : 'worktops-smtp-password';
    
    try {
      setSaving(true);
      
      // Get the actual password value from DOM in case of browser autofill
      const passwordInput = document.querySelector(`[data-testid="${testId}"]`);
      const actualPassword = passwordInput?.value || settings.smtp_password || '';
      
      const dataToSave = {
        smtp_host: settings.smtp_host || null,
        smtp_port: settings.smtp_port || 465,
        smtp_user: settings.smtp_user || null,
        from_email: settings.from_email || null,
        from_name: settings.from_name || null
      };
      
      if (actualPassword) {
        dataToSave.smtp_password = actualPassword;
        console.log(`Saving ${industryType} password (length:`, actualPassword.length, ')');
      } else {
        console.log(`No ${industryType} password to save - field is empty`);
      }
      
      console.log(`Sending ${industryType} email settings:`, Object.keys(dataToSave));
      console.log(`Password included:`, !!dataToSave.smtp_password);
      
      const response = await axios.put(`${BACKEND_URL}/api/companies/${companyId}/email-settings/${industryType}`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log(`Save ${industryType} response:`, response.data);
      
      const industryLabel = industryType.charAt(0).toUpperCase() + industryType.slice(1);
      if (response.data.password_saved) {
        toast.success(`${industryLabel} email settings saved (password updated!)`);
      } else if (actualPassword) {
        toast.warning(`${industryLabel} settings saved but password may not have been updated`);
      } else {
        toast.success(`${industryLabel} email settings saved`);
      }
      setSettings(prev => ({ ...prev, smtp_password: '' }));
      if (passwordInput) passwordInput.value = ''; // Also clear DOM
      fetchData();
    } catch (error) {
      console.error(`Save ${industryType} error:`, error.response?.data || error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : `Failed to save ${industryType} email settings`);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleTestIndustryEmail = async (industryType) => {
    const setTesting = industryType === 'memorials' ? setTestingMemorialsEmail : setTestingWorktopsEmail;
    
    try {
      setTesting(true);
      const res = await axios.post(`${BACKEND_URL}/api/companies/${companyId}/email-settings/${industryType}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Test email sent!');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send test email'); toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  // Branch management functions
  const handleCreateBranch = async () => {
    if (!branchForm.name || !branchForm.short_code) {
      toast.error('Branch name and short code are required');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/branches`, branchForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branch created successfully');
      setShowBranchDialog(false);
      setBranchForm({ name: '', short_code: '', address: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create branch'); toast.error(msg);
    }
  };

  const handleUpdateBranch = async () => {
    if (!editingBranch) return;
    try {
      await axios.put(`${BACKEND_URL}/api/branches/${editingBranch.id}`, branchForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branch updated successfully');
      setShowBranchDialog(false);
      setEditingBranch(null);
      setBranchForm({ name: '', short_code: '', address: '', phone: '', email: '' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update branch'); toast.error(msg);
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Are you sure you want to delete this branch? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/branches/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Branch deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete branch'); toast.error(msg);
    }
  };

  const openEditBranch = (branch) => {
    setEditingBranch(branch);
    setBranchForm({
      name: branch.name || '',
      short_code: branch.short_code || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || ''
    });
    setShowBranchDialog(true);
  };
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${BACKEND_URL}/api/companies/${companyId}/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Logo uploaded');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to upload logo'); toast.error(msg);
    }
  };
  
  const handleLogoRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the company logo?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${companyId}/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logo removed');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to remove logo'); toast.error(msg);
    }
  };
  
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/companies/${companyId}/users?send_welcome_email=true`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created and welcome email sent');
      setShowCreateUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'staff' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to create user'); toast.error(msg);
    }
  };
  
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(`${BACKEND_URL}/api/companies/${companyId}/users/${selectedUser.id}`, {
        name: selectedUser.name,
        email: selectedUser.email,
        role: selectedUser.role,
        branch_id: selectedUser.branch_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated');
      setShowEditUserDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update user'); toast.error(msg);
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${companyId}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to delete user'); toast.error(msg);
    }
  };
  
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/companies/${companyId}/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}&send_email=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password reset and notification sent');
      setShowResetPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to reset password'); toast.error(msg);
    }
  };
  
  const handleSendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast.error('Please enter your feedback message');
      return;
    }
    
    try {
      setSendingFeedback(true);
      await axios.post(
        `${BACKEND_URL}/api/feedback`,
        {
          type: feedbackType,
          message: feedbackMessage,
          company_name: company?.name,
          company_id: companyId,
          user_email: user?.email,
          user_name: user?.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thank you! Your feedback has been sent to Memorial Desk support.');
      setShowFeedbackDialog(false);
      setFeedbackMessage('');
      setFeedbackType('feature');
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send feedback'); toast.error(msg);
    } finally {
      setSendingFeedback(false);
    }
  };
  
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };
  
  const getRoleBadge = (role) => {
    const variants = {
      super_admin: 'bg-purple-100 text-purple-800',
      company_admin: 'bg-blue-100 text-blue-800',
      admin: 'bg-indigo-100 text-indigo-800',
      staff: 'bg-green-100 text-green-800',
      trade: 'bg-amber-100 text-amber-800'
    };
    return <Badge className={variants[role] || 'bg-gray-100 text-gray-800'}>{role}</Badge>;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(isViewingOtherCompany ? '/platform-admin' : '/dashboard')} className="text-slate-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                <h1 className="text-lg font-semibold text-slate-900">
                  {isViewingOtherCompany ? `${company?.name} Settings` : 'Company Settings'}
                </h1>
                {isViewingOtherCompany && (
                  <Badge variant="outline" className="ml-2">Viewing as Admin</Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>Update your company details and branding</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Company Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Company Name Ltd"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="01234 567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://www.company.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vat_number">VAT Number</Label>
                    <Input
                      id="vat_number"
                      value={formData.vat_number}
                      onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                      placeholder="GB123456789"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bramm_number">BRAMM Registration Number</Label>
                    <Input
                      id="bramm_number"
                      value={formData.bramm_number}
                      onChange={(e) => setFormData({ ...formData, bramm_number: e.target.value })}
                      placeholder="Used automatically on memorial applications"
                      data-testid="company-bramm-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="namm_number">NAMM Registration Number</Label>
                    <Input
                      id="namm_number"
                      value={formData.namm_number}
                      onChange={(e) => setFormData({ ...formData, namm_number: e.target.value })}
                      placeholder="Used automatically on memorial applications"
                      data-testid="company-namm-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 High Street&#10;Town&#10;County&#10;Postcode"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="email_footer">Email Footer</Label>
                  <Textarea
                    id="email_footer"
                    value={formData.email_footer}
                    onChange={(e) => setFormData({ ...formData, email_footer: e.target.value })}
                    placeholder="Company Name Ltd - Quality Memorials Since 1990"
                    rows={2}
                  />
                  <p className="text-xs text-slate-500 mt-1">This text appears at the bottom of emails sent to customers</p>
                </div>
                
                <div className="flex justify-end">
                  <Button onClick={handleSaveCompany} disabled={saving}>
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Family Page Settings */}
            <Card data-testid="family-page-settings-card">
              <CardHeader>
                <CardTitle>Family Pages</CardTitle>
                <CardDescription>Private online pages for families to track their memorial — included in your subscription</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                  <div>
                    <Label className="text-base">Use Family Pages</Label>
                    <p className="text-sm text-slate-500 mt-1">
                      Give each family a private, no-login page to follow progress, see the design, share memories and message you. This is included free in your monthly subscription.
                    </p>
                  </div>
                  <Switch
                    checked={familyFeatureEnabled}
                    onCheckedChange={handleToggleFamilyFeature}
                    disabled={savingFamilyDefault}
                    data-testid="family-feature-switch"
                  />
                </div>
                {familyFeatureEnabled && (
                  <div className="flex items-center justify-between gap-4 rounded-lg border p-4 bg-slate-50">
                    <div>
                      <Label className="text-base">Auto-create for new memorial orders</Label>
                      <p className="text-sm text-slate-500 mt-1">
                        When on, every new memorial order gets a family page automatically. You can still toggle it off per order on the create form, or disable it later from the order.
                      </p>
                    </div>
                    <Switch
                      checked={familyAutoEnable}
                      onCheckedChange={handleToggleFamilyDefault}
                      disabled={savingFamilyDefault}
                      data-testid="family-auto-enable-switch"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Users Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Team Members</CardTitle>
                  <CardDescription>Manage users who have access to this company</CardDescription>
                </div>
                <Button onClick={() => {
                  setNewUser({ name: '', email: '', password: generatePassword(), role: 'staff' });
                  setShowCreateUserDialog(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <div key={u.id} className="py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                        {getRoleBadge(u.role)}
                        {u.id === user?.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                      </div>
                      
                      {u.id !== user?.id && u.role !== 'super_admin' && (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedUser({ ...u });
                              setShowEditUserDialog(true);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedUser(u);
                              setNewPassword(generatePassword());
                              setShowResetPasswordDialog(true);
                            }}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteUser(u.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {users.length === 0 && (
                    <div className="py-8 text-center text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No team members yet</p>
                      <p className="text-sm">Add users to give them access to this company</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Branch Management Section */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-teal-600" />
                    Branch Management
                  </CardTitle>
                  <CardDescription>Manage your company locations and branches</CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    setEditingBranch(null);
                    setBranchForm({ name: '', short_code: '', address: '', phone: '', email: '' });
                    setShowBranchDialog(true);
                  }}
                  disabled={branchLimits && branches.length >= branchLimits.total_limit}
                  data-testid="add-branch-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Branch
                </Button>
              </CardHeader>
              <CardContent>
                {/* Subscription Limits Info */}
                {branchLimits && (
                  <div className={`mb-4 p-3 rounded-lg ${
                    branchLimits.tier === 'super_admin' 
                      ? 'bg-purple-50 border border-purple-200'
                      : branches.length >= branchLimits.total_limit 
                        ? 'bg-amber-50 border border-amber-200' 
                        : 'bg-teal-50 border border-teal-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {branchLimits.tier === 'super_admin' 
                          ? `Branches: ${branches.length} (Unlimited)`
                          : `Branches: ${branches.length} / ${branchLimits.total_limit}`
                        }
                      </span>
                      <Badge className={
                        branchLimits.tier === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                        branchLimits.tier === 'premium' ? 'bg-purple-100 text-purple-800' :
                        branchLimits.tier === 'standard' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }>
                        {branchLimits.tier === 'super_admin' ? 'Super Admin' : (branchLimits.tier || 'Basic') + ' Plan'}
                      </Badge>
                    </div>
                    {branches.length >= branchLimits.total_limit && branchLimits.tier !== 'premium' && branchLimits.tier !== 'super_admin' && (
                      <p className="text-xs text-amber-700 mt-2">
                        {branchLimits.tier === 'standard' 
                          ? "You've reached your branch limit. Additional branches cost £5/month each."
                          : "Upgrade to Standard or Premium to add more branches."}
                      </p>
                    )}
                    {branchLimits.tier === 'standard' && branchLimits.additional_branches_paid > 0 && (
                      <p className="text-xs text-teal-700 mt-1">
                        +{branchLimits.additional_branches_paid} additional branch(es) purchased
                      </p>
                    )}
                  </div>
                )}
                
                {/* Branch List */}
                <div className="divide-y divide-slate-100">
                  {branches.map((branch) => (
                    <div key={branch.id} className="py-4" data-testid={`branch-item-${branch.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            branch.is_main 
                              ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {branch.short_code ? (
                              <span className="font-bold text-sm">{branch.short_code.toUpperCase()}</span>
                            ) : branch.is_main ? (
                              <Crown className="h-5 w-5" />
                            ) : (
                              <MapPin className="h-5 w-5" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{branch.name}</p>
                              {branch.short_code && (
                                <Badge variant="outline" className="text-xs font-mono">
                                  Orders: {branch.short_code.toUpperCase()}26xxx
                                </Badge>
                              )}
                              {branch.is_main && (
                                <Badge className="bg-teal-100 text-teal-800 text-xs">Main Branch</Badge>
                              )}
                              {!branch.is_active && (
                                <Badge variant="outline" className="text-slate-500 text-xs">Inactive</Badge>
                              )}
                            </div>
                            {branch.address && (
                              <p className="text-sm text-slate-500 mt-1">{branch.address}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              {branch.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {branch.phone}
                                </span>
                              )}
                              {branch.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {branch.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditBranch(branch)}
                            data-testid={`edit-branch-${branch.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {!branch.is_main && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteBranch(branch.id)}
                              data-testid={`delete-branch-${branch.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {branches.length === 0 && (
                    <div className="py-8 text-center text-slate-500">
                      <MapPin className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No branches yet</p>
                      <p className="text-sm">Add your first branch to get started</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Logo - only for white-label enabled users */}
            {(company?.features?.white_label_enabled || 
              company?.subscription?.tier === 'standard' || 
              company?.subscription?.tier === 'premium' ||
              user?.role === 'super_admin') ? (
              <Card>
                <CardHeader>
                  <CardTitle>Company Logo</CardTitle>
                  <CardDescription>Upload your company logo for documents and emails</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center">
                    {company?.branding?.logo_data ? (
                      <img 
                        src={company.branding.logo_data} 
                        alt="Company Logo" 
                        className="max-h-32 max-w-full mb-4 rounded"
                    />
                  ) : company?.branding?.logo_url ? (
                    <img 
                      src={company.branding.logo_url} 
                      alt="Company Logo" 
                      className="max-h-32 max-w-full mb-4 rounded"
                    />
                  ) : (
                    <div className="h-32 w-32 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                      <Building2 className="h-12 w-12 text-slate-300" />
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Logo
                    </Button>
                    {(company?.branding?.logo_data || company?.branding?.logo_url) && (
                      <Button variant="outline" onClick={handleLogoRemove} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            ) : (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-500">Company Logo</CardTitle>
                  <CardDescription>
                    Logo upload is available with Standard or Premium plans
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center py-4">
                    <div className="h-20 w-20 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                      <Building2 className="h-8 w-8 text-slate-300" />
                    </div>
                    <p className="text-xs text-slate-500 text-center">
                      Basic plan: Memorial Desk branding will appear on your documents
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Branding Settings - White Label (only for standard/premium tiers) */}
            {(company?.features?.white_label_enabled || 
              company?.subscription?.tier === 'standard' || 
              company?.subscription?.tier === 'premium' ||
              user?.role === 'super_admin') ? (
              <Card>
                <CardHeader>
                  <CardTitle>White Label Branding</CardTitle>
                  <CardDescription>
                    Customize how your company appears on quotes, invoices, and emails sent to clients.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Company Tagline</Label>
                    <Input
                      value={brandingData.tagline}
                      onChange={(e) => setBrandingData(prev => ({ ...prev, tagline: e.target.value }))}
                      placeholder="e.g., Handcrafted Memorials"
                    />
                    <p className="text-xs text-slate-500">Shown below your company name in documents</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Email Footer Text</Label>
                    <Textarea
                      value={brandingData.footer_text}
                      onChange={(e) => setBrandingData(prev => ({ ...prev, footer_text: e.target.value }))}
                      placeholder="If you have any questions, please don't hesitate to contact us."
                      rows={2}
                    />
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">Payment Details (shown on invoices)</h4>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Bank Account Name</Label>
                        <Input
                          value={brandingData.bank_name}
                          onChange={(e) => setBrandingData(prev => ({ ...prev, bank_name: e.target.value }))}
                          placeholder="Your Company Ltd"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Account Number</Label>
                          <Input
                            value={brandingData.bank_account}
                            onChange={(e) => setBrandingData(prev => ({ ...prev, bank_account: e.target.value }))}
                            placeholder="12345678"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sort Code</Label>
                          <Input
                            value={brandingData.sort_code}
                            onChange={(e) => setBrandingData(prev => ({ ...prev, sort_code: e.target.value }))}
                            placeholder="12-34-56"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleSaveBranding} 
                    disabled={savingBranding}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingBranding ? 'Saving...' : 'Save Branding Settings'}
                </Button>
              </CardContent>
            </Card>
            ) : (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-600" />
                    White Label Branding
                  </CardTitle>
                  <CardDescription>
                    Upgrade to Standard or Premium to customize your branding on quotes, invoices, and emails.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <p className="text-sm text-slate-600 mb-4">
                      Basic plan users see <strong>Memorial Desk</strong> branding on all client-facing documents.
                      Upgrade to add your own logo, company name, and payment details.
                    </p>
                    <Button 
                      onClick={() => navigate('/subscription')}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Upgrade for White Label
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Subscription & Billing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscription & Billing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Plan */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-500">Current Plan</span>
                    <Badge className={
                      company?.subscription?.tier === 'premium' ? 'bg-purple-100 text-purple-800' :
                      company?.subscription?.tier === 'standard' ? 'bg-blue-100 text-blue-800' :
                      company?.subscription?.tier === 'trial' ? 'bg-amber-100 text-amber-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {company?.subscription?.tier === 'trial' ? '🆓 Free Trial' : 
                       company?.subscription?.tier === 'premium' ? '⭐ Premium' :
                       company?.subscription?.tier === 'standard' ? 'Standard' :
                       company?.subscription?.tier || 'Basic'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Status</span>
                    <Badge className={
                      company?.status === 'active' ? 'bg-green-100 text-green-800' :
                      company?.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {company?.status === 'active' ? '✓ Active' : 
                       company?.status === 'trial' ? '🔵 Trial' : 
                       company?.status || 'Unknown'}
                    </Badge>
                  </div>
                </div>
                
                {/* Trial Warning */}
                {company?.subscription?.tier === 'trial' && (
                  <div className={`p-3 rounded-lg flex items-start gap-2 ${
                    getTrialDaysRemaining() <= 3 ? 'bg-red-50 border border-red-200' :
                    getTrialDaysRemaining() <= 7 ? 'bg-amber-50 border border-amber-200' :
                    'bg-blue-50 border border-blue-200'
                  }`}>
                    {getTrialDaysRemaining() <= 3 ? (
                      <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Calendar className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${
                        getTrialDaysRemaining() <= 3 ? 'text-red-800' :
                        getTrialDaysRemaining() <= 7 ? 'text-amber-800' :
                        'text-blue-800'
                      }`}>
                        {getTrialDaysRemaining()} days remaining
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Trial ends {new Date(company.subscription.trial_ends_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Subscription End Date (for paid plans) */}
                {company?.subscription?.subscription_ends_at && company?.subscription?.tier !== 'trial' && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Renews On</span>
                      <span className="text-sm font-medium">
                        {new Date(company.subscription.subscription_ends_at).toLocaleDateString()}
                      </span>
                    </div>
                    {company?.subscription?.monthly_price && (
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-slate-500">Monthly Price</span>
                        <span className="text-sm font-medium">£{company.subscription.monthly_price}/mo</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="space-y-2 pt-2">
                  {/* Change/Upgrade Plan Button */}
                  <Button 
                    className="w-full" 
                    variant={company?.subscription?.tier === 'trial' ? 'default' : 'outline'}
                    onClick={() => navigate('/subscription')}
                    data-testid="upgrade-plan-btn"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {company?.subscription?.tier === 'trial' ? 'Upgrade Now' : 'Change Plan'}
                  </Button>
                  
                  {/* Manage Billing Button - only show for paid subscribers */}
                  {company?.subscription?.tier && company?.subscription?.tier !== 'trial' && (
                    <Button 
                      className="w-full" 
                      variant="outline"
                      onClick={handleOpenBillingPortal}
                      disabled={openingPortal}
                      data-testid="manage-billing-btn"
                    >
                      {openingPortal ? (
                        <>Loading...</>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Manage Billing
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                <p className="text-xs text-slate-400 text-center">
                  Secure payments powered by Stripe
                </p>
              </CardContent>
            </Card>
            
            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle>Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { key: 'worktops_enabled', label: 'Worktop Orders' },
                    { key: 'stock_management_enabled', label: 'Stock Management' },
                    { key: 'artwork_tools_enabled', label: 'Artwork Tools' },
                    { key: 'email_sending_enabled', label: 'Email Sending' },
                    { key: 'grave_vaults_enabled', label: 'Grave Vaults' },
                    { key: 'corel_integration_enabled', label: 'Corel Integration' }
                  ].map((feature) => (
                    <div key={feature.key} className="flex items-center justify-between py-1">
                      <span className="text-sm text-slate-600">{feature.label}</span>
                      {company?.features?.[feature.key] ? (
                        <UserCheck className="h-4 w-4 text-green-500" />
                      ) : (
                        <UserX className="h-4 w-4 text-slate-300" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-4">Contact your platform administrator to change features</p>
              </CardContent>
            </Card>
            
            {/* Email Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Email Settings (SMTP)
                </CardTitle>
                <CardDescription>
                  Configure your company's email server to send emails from your own domain
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {emailSettingsConfigured ? (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                    <UserCheck className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-800">Email settings configured</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-800">Using platform email settings</span>
                  </div>
                )}
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      value={emailSettings.smtp_host}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                      placeholder="e.g., smtp.gmail.com or mail.yourdomain.com"
                      data-testid="smtp-host-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>SMTP Port</Label>
                      <Select
                        value={String(emailSettings.smtp_port)}
                        onValueChange={(val) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(val) }))}
                      >
                        <SelectTrigger data-testid="smtp-port-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="465">465 (SSL)</SelectItem>
                          <SelectItem value="587">587 (STARTTLS)</SelectItem>
                          <SelectItem value="25">25 (Standard)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>From Name</Label>
                      <Input
                        value={emailSettings.from_name}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, from_name: e.target.value }))}
                        placeholder="Your Company Name"
                        data-testid="from-name-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>SMTP Username / Email</Label>
                    <Input
                      value={emailSettings.smtp_user}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                      placeholder="email@yourdomain.com"
                      data-testid="smtp-user-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>SMTP Password</Label>
                    <div className="relative">
                      <Input
                        type={showSmtpPassword ? 'text' : 'password'}
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                        placeholder={emailSettingsConfigured ? '••••••••••••' : 'Enter password'}
                        data-testid="smtp-password-input"
                        autoComplete="new-password"
                        name="smtp_password_field"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      >
                        {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    {emailSettingsConfigured && (
                      <p className="text-xs text-slate-500">Leave blank to keep existing password</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>From Email (optional)</Label>
                    <Input
                      value={emailSettings.from_email}
                      onChange={(e) => setEmailSettings(prev => ({ ...prev, from_email: e.target.value }))}
                      placeholder="Same as SMTP username if blank"
                      data-testid="from-email-input"
                    />
                    <p className="text-xs text-slate-500">The email address that will appear in the "From" field</p>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={handleSaveEmailSettings}
                    disabled={savingEmailSettings}
                    className="flex-1"
                    data-testid="save-email-settings-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {savingEmailSettings ? 'Saving...' : 'Save Settings'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={testingEmail || !emailSettingsConfigured}
                    data-testid="test-email-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {testingEmail ? 'Sending...' : 'Test'}
                  </Button>
                </div>
                
                <p className="text-xs text-slate-400 text-center">
                  Leave blank to use Memorial Desk platform email
                </p>
              </CardContent>
            </Card>
            
            {/* Industry-Specific Email Settings - Only for companies handling both */}
            {company?.industry_type === 'both' && (
              <>
                {/* Memorials Email Settings */}
                <Card className="border-indigo-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                      <Mail className="h-5 w-5" />
                      Memorials Email Settings
                    </CardTitle>
                    <CardDescription>SMTP settings for memorial orders and quotes</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {emailSettingsMemorialsConfigured ? (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                        <UserCheck className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-800">Memorials email configured</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <span className="text-sm text-amber-800">Using default settings</span>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">SMTP Host</Label>
                        <Input
                          value={emailSettingsMemorials.smtp_host}
                          onChange={(e) => setEmailSettingsMemorials(prev => ({ ...prev, smtp_host: e.target.value }))}
                          placeholder="smtp.host.com"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Port</Label>
                        <Select
                          value={String(emailSettingsMemorials.smtp_port)}
                          onValueChange={(val) => setEmailSettingsMemorials(prev => ({ ...prev, smtp_port: parseInt(val) }))}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="465">465 (SSL)</SelectItem>
                            <SelectItem value="587">587 (STARTTLS)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">SMTP Username</Label>
                      <Input
                        value={emailSettingsMemorials.smtp_user}
                        onChange={(e) => setEmailSettingsMemorials(prev => ({ ...prev, smtp_user: e.target.value }))}
                        placeholder="email@domain.com"
                        className="h-9"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Password</Label>
                      <div className="relative">
                        <Input
                          type={showMemorialsPassword ? 'text' : 'password'}
                          value={emailSettingsMemorials.smtp_password}
                          onChange={(e) => setEmailSettingsMemorials(prev => ({ ...prev, smtp_password: e.target.value }))}
                          placeholder={emailSettingsMemorialsConfigured ? '••••••••' : 'Enter password'}
                          className="h-9"
                          autoComplete="new-password"
                          name="memorials_smtp_password"
                          data-testid="memorials-smtp-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowMemorialsPassword(!showMemorialsPassword)}
                        >
                          {showMemorialsPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">From Email</Label>
                        <Input
                          value={emailSettingsMemorials.from_email}
                          onChange={(e) => setEmailSettingsMemorials(prev => ({ ...prev, from_email: e.target.value }))}
                          placeholder="Optional"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">From Name</Label>
                        <Input
                          value={emailSettingsMemorials.from_name}
                          onChange={(e) => setEmailSettingsMemorials(prev => ({ ...prev, from_name: e.target.value }))}
                          placeholder="Company Name"
                          className="h-9"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-1">
                      <Button 
                        onClick={() => handleSaveIndustryEmailSettings('memorials')}
                        disabled={savingMemorialsEmail}
                        size="sm"
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        {savingMemorialsEmail ? 'Saving...' : 'Save'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleTestIndustryEmail('memorials')}
                        disabled={testingMemorialsEmail || !emailSettingsMemorialsConfigured}
                        size="sm"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {testingMemorialsEmail ? '...' : 'Test'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Worktops Email Settings */}
                <Card className="border-emerald-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-emerald-800">
                      <Mail className="h-5 w-5" />
                      Worktops Email Settings
                    </CardTitle>
                    <CardDescription>SMTP settings for worktop orders and quotes</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {emailSettingsWorktopsConfigured ? (
                      <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                        <UserCheck className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-800">Worktops email configured</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <span className="text-sm text-amber-800">Using default settings</span>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">SMTP Host</Label>
                        <Input
                          value={emailSettingsWorktops.smtp_host}
                          onChange={(e) => setEmailSettingsWorktops(prev => ({ ...prev, smtp_host: e.target.value }))}
                          placeholder="smtp.host.com"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Port</Label>
                        <Select
                          value={String(emailSettingsWorktops.smtp_port)}
                          onValueChange={(val) => setEmailSettingsWorktops(prev => ({ ...prev, smtp_port: parseInt(val) }))}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="465">465 (SSL)</SelectItem>
                            <SelectItem value="587">587 (STARTTLS)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">SMTP Username</Label>
                      <Input
                        value={emailSettingsWorktops.smtp_user}
                        onChange={(e) => setEmailSettingsWorktops(prev => ({ ...prev, smtp_user: e.target.value }))}
                        placeholder="email@domain.com"
                        className="h-9"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Password</Label>
                      <div className="relative">
                        <Input
                          type={showWorktopsPassword ? 'text' : 'password'}
                          value={emailSettingsWorktops.smtp_password}
                          onChange={(e) => setEmailSettingsWorktops(prev => ({ ...prev, smtp_password: e.target.value }))}
                          placeholder={emailSettingsWorktopsConfigured ? '••••••••' : 'Enter password'}
                          className="h-9"
                          autoComplete="new-password"
                          name="worktops_smtp_password"
                          data-testid="worktops-smtp-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setShowWorktopsPassword(!showWorktopsPassword)}
                        >
                          {showWorktopsPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">From Email</Label>
                        <Input
                          value={emailSettingsWorktops.from_email}
                          onChange={(e) => setEmailSettingsWorktops(prev => ({ ...prev, from_email: e.target.value }))}
                          placeholder="Optional"
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">From Name</Label>
                        <Input
                          value={emailSettingsWorktops.from_name}
                          onChange={(e) => setEmailSettingsWorktops(prev => ({ ...prev, from_name: e.target.value }))}
                          placeholder="Company Name"
                          className="h-9"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-1">
                      <Button 
                        onClick={() => handleSaveIndustryEmailSettings('worktops')}
                        disabled={savingWorktopsEmail}
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        {savingWorktopsEmail ? 'Saving...' : 'Save'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleTestIndustryEmail('worktops')}
                        disabled={testingWorktopsEmail || !emailSettingsWorktopsConfigured}
                        size="sm"
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {testingWorktopsEmail ? '...' : 'Test'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
            
            {/* Feedback & Support - only show for non-super-admin users */}
            {user?.role !== 'super_admin' && (
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <MessageSquare className="h-5 w-5" />
                    Feedback & Support
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Have a feature request, bug report, or suggestion? Let us know!
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => setShowFeedbackDialog(true)}
                  >
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Send Feedback
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      
      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Create a new user account. They will receive a welcome email with their login details.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-name">Full Name *</Label>
              <Input
                id="new-name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            
            <div>
              <Label htmlFor="new-email">Email *</Label>
              <Input
                id="new-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@company.com"
              />
            </div>
            
            <div>
              <Label htmlFor="new-password">Temporary Password *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setNewUser({ ...newUser, password: generatePassword() })}>
                  Generate
                </Button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="new-role">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="workshop">Workshop / Fitter</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  {user?.role === 'super_admin' && (
                    <SelectItem value="company_admin">Company Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUserDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateUser}>
              <Mail className="h-4 w-4 mr-2" />
              Create & Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={selectedUser.email}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <Select value={selectedUser.role} onValueChange={(v) => setSelectedUser({ ...selectedUser, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="workshop">Workshop / Fitter</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    {user?.role === 'super_admin' && (
                      <>
                        <SelectItem value="company_admin">Company Admin</SelectItem>
                        <SelectItem value="super_admin">Super Admin</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Branch Assignment */}
              {branches.length > 0 && (
                <div>
                  <Label htmlFor="edit-branch">Assigned Branch</Label>
                  <Select 
                    value={selectedUser.branch_id || 'all'} 
                    onValueChange={(v) => setSelectedUser({ ...selectedUser, branch_id: v === 'all' ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches (Full Access)</SelectItem>
                      {branches.filter(b => b.is_active !== false).map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name} ({branch.short_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Staff assigned to a branch will only see that branch's orders
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name}. They will receive an email with the new password.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="reset-password">New Password</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <Button variant="outline" onClick={() => setNewPassword(generatePassword())}>
                Generate
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleResetPassword}>
              <Key className="h-4 w-4 mr-2" />
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Send Feedback to Memorial Desk
            </DialogTitle>
            <DialogDescription>
              Your feedback helps us improve. We'll review and respond to your message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>What type of feedback is this?</Label>
              <Select value={feedbackType} onValueChange={setFeedbackType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">
                    <span className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-amber-500" />
                      Feature Request
                    </span>
                  </SelectItem>
                  <SelectItem value="bug">
                    <span className="flex items-center gap-2">
                      <Bug className="h-4 w-4 text-red-500" />
                      Bug Report
                    </span>
                  </SelectItem>
                  <SelectItem value="other">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-500" />
                      General Feedback
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Your Message</Label>
              <Textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder={
                  feedbackType === 'feature' ? "Describe the feature you'd like to see..." :
                  feedbackType === 'bug' ? "Describe the issue you're experiencing. Include steps to reproduce if possible..." :
                  "Share your thoughts or suggestions..."
                }
                rows={5}
              />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
              <p><strong>From:</strong> {user?.name} ({user?.email})</p>
              <p><strong>Company:</strong> {company?.name}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendFeedback}
              disabled={sendingFeedback || !feedbackMessage.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingFeedback ? (
                'Sending...'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Feedback
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Branch Dialog */}
      <Dialog open={showBranchDialog} onOpenChange={setShowBranchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-teal-600" />
              {editingBranch ? 'Edit Branch' : 'Add New Branch'}
            </DialogTitle>
            <DialogDescription>
              {editingBranch 
                ? 'Update the details for this branch location.'
                : 'Add a new branch or location for your company.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="branch-name">Branch Name *</Label>
                <Input
                  id="branch-name"
                  value={branchForm.name}
                  onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
                  placeholder="e.g., Oxford Branch, Didcot Office"
                  data-testid="branch-name-input"
                />
              </div>
              <div>
                <Label htmlFor="branch-short-code">Short Code *</Label>
                <Input
                  id="branch-short-code"
                  value={branchForm.short_code}
                  onChange={(e) => setBranchForm({ ...branchForm, short_code: e.target.value.toUpperCase().slice(0, 3) })}
                  placeholder="e.g., O, OX, DID"
                  maxLength={3}
                  className="uppercase font-mono"
                  data-testid="branch-short-code-input"
                />
                <p className="text-xs text-slate-500 mt-1">1-3 letters for order numbers</p>
              </div>
            </div>
            
            <div>
              <Label htmlFor="branch-address">Address</Label>
              <Textarea
                id="branch-address"
                value={branchForm.address}
                onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                placeholder="123 High Street&#10;Town&#10;County&#10;Postcode"
                rows={3}
                data-testid="branch-address-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="branch-phone">Phone</Label>
                <Input
                  id="branch-phone"
                  value={branchForm.phone}
                  onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                  placeholder="01234 567890"
                  data-testid="branch-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="branch-email">Email</Label>
                <Input
                  id="branch-email"
                  type="email"
                  value={branchForm.email}
                  onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })}
                  placeholder="branch@company.com"
                  data-testid="branch-email-input"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowBranchDialog(false);
              setEditingBranch(null);
              setBranchForm({ name: '', short_code: '', address: '', phone: '', email: '' });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={editingBranch ? handleUpdateBranch : handleCreateBranch}
              disabled={!branchForm.name.trim() || !branchForm.short_code.trim()}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-branch-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingBranch ? 'Update Branch' : 'Create Branch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
